import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiDollarSign, FiMessageCircle, FiTrendingUp, FiCalendar } from 'react-icons/fi';
import { formatMoney } from '../utils/money';
import { fetchCollectionCycleMonth } from '../utils/collectionCycleMonthClient';
function CycleSkeleton({ box, modoOscuro, compact }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className={`rounded-2xl border p-4 ${box}`}>
      <div className={`h-4 w-56 rounded mb-3 ${pulse}`} />
      <div className={`grid gap-2 ${compact ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-14 rounded-xl ${pulse}`} />
        ))}
      </div>
    </div>
  );
}

function MetricBox({ label, value, sub, modoOscuro, tone = 'default' }) {
  const tile = modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50';
  const toneClass = tone === 'green'
    ? modoOscuro ? 'text-green-300' : 'text-green-700'
    : tone === 'amber'
      ? modoOscuro ? 'text-amber-300' : 'text-amber-700'
      : modoOscuro ? 'text-white' : 'text-gray-900';
  return (
    <div className={`rounded-xl px-3 py-2.5 ${tile}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-base sm:text-lg font-semibold tabular-nums mt-0.5 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5 text-gray-400">{sub}</div>}
    </div>
  );
}

/**
 * Resumen C10.4: ciclo de cobro del mes (Inicio + Cobrar).
 * @param {object} [hubSummary] — si viene del hub de Cobranzas, evita duplicar pendiente/impagas en pantalla.
 */
export default function CollectionCycleMonthCard({
  secureFetch,
  hasProfesional,
  modoOscuro,
  box,
  textPri,
  textSec,
  textMuted,
  compact = false,
  hubSummary = null,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const fetchCycle = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchCollectionCycleMonth(secureFetch));
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    fetchCycle();
  }, [fetchCycle]);

  if (loading) return <CycleSkeleton box={box} modoOscuro={modoOscuro} compact={compact} />;

  if (error) {
    return (
      <div className={`rounded-2xl border px-4 py-3 text-sm ${box} ${modoOscuro ? 'text-red-300' : 'text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!data?.metrics) return null;

  const m = data.metrics;
  const pendiente = hubSummary?.totalPendienteArs != null
    ? hubSummary.totalPendienteArs
    : m.pendienteArs;
  const impagas = hubSummary?.impagas != null ? hubSummary.impagas : m.impagas;
  const promesasActivas = hubSummary?.promesasProximas != null
    ? hubSummary.promesasProximas
    : m.promesasActivas;
  const promesasVencidas = hubSummary?.promesasVencidas != null
    ? hubSummary.promesasVencidas
    : m.promesasVencidas;

  const promesaSub = promesasVencidas > 0
    ? `${promesasVencidas} vencida(s)`
    : promesasActivas > 0
      ? `${promesasActivas} activa(s)`
      : 'Sin promesas';

  const hit = data.impact?.recentHit;

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`} id="ciclo-cobro-mes">
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
              }`}>
                <FiDollarSign className="w-3 h-3" />
                Este mes en cobranzas
              </span>
              <span className={`text-[10px] ${textMuted}`}>{data.period?.label}</span>
            </div>
            <h2 className={`text-sm font-semibold ${textPri}`}>
              {m.recoveredArs > 0
                ? `Recuperaste ${formatMoney(m.recoveredArs, 'ARS')} tras tus gestiones`
                : 'Tu ciclo de cobro del mes'}
            </h2>
            <p className={`text-xs mt-0.5 ${textSec}`}>{data.loopTagline}</p>
          </div>
          {!compact && (
            <button
              type="button"
              onClick={() => navigate('/cobranzas')}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#1F80FF] hover:underline"
            >
              Ir a Cobrar
              <FiArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <MetricBox
            label="Por cobrar"
            value={formatMoney(pendiente, 'ARS')}
            sub={impagas > 0 ? `${impagas} impaga(s)` : 'Al día'}
            modoOscuro={modoOscuro}
            tone={impagas > 0 ? 'amber' : 'default'}
          />
          <MetricBox
            label="Gestiones"
            value={m.gestionesMes}
            sub="Email, WA o llamada"
            modoOscuro={modoOscuro}
          />
          <MetricBox
            label="Recuperado"
            value={formatMoney(m.recoveredArs, 'ARS')}
            sub={m.paymentsAttributed > 0 ? `${m.paymentsAttributed} pago(s) atribuido(s)` : `Ventana ${data.impact?.attributionDays || 14}d`}
            modoOscuro={modoOscuro}
            tone={m.recoveredArs > 0 ? 'green' : 'default'}
          />
          <MetricBox
            label="Promesas"
            value={promesasActivas + promesasVencidas}
            sub={promesaSub}
            modoOscuro={modoOscuro}
            tone={promesasVencidas > 0 ? 'amber' : 'default'}
          />
        </div>

        {!compact && hit && (
          <p className={`text-xs flex flex-wrap items-center gap-1.5 ${textMuted}`}>
            <FiTrendingUp className="w-3.5 h-3.5 text-green-500" />
            Último atribuido: N°{hit.numero} · {hit.cliente} · +{formatMoney(hit.montoArs, 'ARS')}
          </p>
        )}

        {compact && (
          <div className="flex flex-wrap items-center gap-2 text-[11px]">
            <span className={`inline-flex items-center gap-1 ${textMuted}`}>
              <FiMessageCircle className="w-3.5 h-3.5" />
              {m.gestionesMes} gestión(es)
            </span>
            <span className={`inline-flex items-center gap-1 ${textMuted}`}>
              <FiCalendar className="w-3.5 h-3.5" />
              {promesaSub}
            </span>
            <button
              type="button"
              onClick={() => navigate('/reportes?tab=cobranzas')}
              className="font-semibold text-[#1F80FF] hover:underline ml-auto"
            >
              Ver análisis
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
